<script setup>
/**
 * One note as its owner manages it: the note — words, person, card — edited in
 * place (Kapil, 2026-09-23: "I'd just want to see everything I have written…
 * and to be able to edit each one right in line"), and the surface's own line
 * under it.
 *
 * One note, one text (Kapil, 2026-09-24: "it can only be a private note or has
 * to be a highlight… it can't be both"). A note that is shared IS the
 * Highlight, so there is one Edit and one status, never a second version.
 *
 * The body is ProfileEntryBody, as everywhere; the rules are in
 * composables/useNoteEdit (unit-tested).
 */
import axios from '@axios'
import { NOTE_MAX_LENGTH } from '@/utils/profileNotes'
import { useNoteEdit } from '@/composables/useNoteEdit'
import ProfileEntryBody from '@core/components/ProfileEntryBody.vue'

const props = defineProps({
  entry: { type: Object, required: true },
  restaurantName: { type: String, default: '' },
  textClass: { type: String, default: '' },

  // Set by the surface's Edit.
  editing: { type: Boolean, default: false },
})

const emit = defineEmits(['changed', 'done', 'open-card'])

const { draft, saving, error, save } = useNoteEdit(props, { http: axios, emit })
</script>

<template>
  <ProfileEntryBody
    :entry="entry"
    :restaurant-name="restaurantName"
    :text-class="textClass"
    @open-card="emit('open-card', $event)"
  >
    <template
      v-if="editing"
      #words
    >
      <div class="note-item__editor">
        <VTextarea
          v-model="draft"
          :maxlength="NOTE_MAX_LENGTH"
          rows="2"
          auto-grow
          autofocus
          variant="outlined"
          density="compact"
          hide-details
          class="note-item__input"
        />
        <div class="note-item__editor-actions">
          <VBtn
            variant="text"
            size="small"
            @click="emit('done')"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            variant="flat"
            size="small"
            rounded="lg"
            :loading="saving"
            :disabled="!draft.trim()"
            @click="save"
          >
            Save
          </VBtn>
        </div>
      </div>
    </template>
  </ProfileEntryBody>

  <!-- The note's own line — when, what it is, and its actions — right under it. -->
  <slot name="meta" />

  <p
    v-if="error"
    class="note-item__error"
  >
    {{ error }}
  </p>
</template>

<style scoped>
/* iOS must not zoom the page when a box takes focus. */
.note-item__input :deep(textarea) {
  font-size: 16px;
}

.note-item__editor {
  margin-top: 4px;
}

.note-item__editor-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.note-item__error {
  margin: 4px 0 0;
  font-size: 12px;
  color: rgb(var(--v-theme-error));
}
</style>
